/**
 * Navigation model of a Data Lake view tab (S8 Task 8.9): breadcrumb segments, the parent location, root containment
 * and the folder-row → directory mapping under an `hnsPath` root. Locations are always directories (`isDirectory:
 * true`); navigation never leaves the root (spec §6.1).
 */
import type { HnsPathRef, StorageResourceRef } from '../../../../types/azureStorage';
import { hnsDirectoryOfBlobRoot } from './hnsView';

type Segment = { label: string; ref: StorageResourceRef };

const lastSegment = (path: string): string => path.slice(path.lastIndexOf('/') + 1);
const directory = (fileSystem: string, path: string): HnsPathRef => ({ kind: 'hnsPath', fileSystem, path, isDirectory: true });

export function isWithinHnsRoot(root: HnsPathRef, resource: StorageResourceRef): boolean {
  if (resource.kind !== 'hnsPath' || !resource.isDirectory || resource.fileSystem !== root.fileSystem) return false;
  return root.path === '' || resource.path === root.path || resource.path.startsWith(`${root.path}/`);
}

export function hnsBreadcrumbSegments(root: HnsPathRef, resource: StorageResourceRef): Segment[] {
  const segments: Segment[] = [{ label: root.path ? lastSegment(root.path) : root.fileSystem, ref: root }];
  if (!isWithinHnsRoot(root, resource) || resource.kind !== 'hnsPath' || resource.path === root.path) return segments;
  const relative = root.path ? resource.path.slice(root.path.length + 1) : resource.path;
  let cumulative = root.path;
  for (const part of relative.split('/').filter(Boolean)) {
    cumulative = cumulative ? `${cumulative}/${part}` : part;
    segments.push({ label: part, ref: directory(root.fileSystem, cumulative) });
  }
  return segments;
}

export function hnsParentRef(root: HnsPathRef, resource: StorageResourceRef): StorageResourceRef | null {
  if (!isWithinHnsRoot(root, resource) || resource.kind !== 'hnsPath' || resource.path === root.path) return null;
  const index = resource.path.lastIndexOf('/');
  const parent = index === -1 ? '' : resource.path.slice(0, index);
  return parent.length <= root.path.length ? root : directory(root.fileSystem, parent);
}

/** A folder row's Blob ref (`blobPrefix`) → the equivalent directory under the `hnsPath` root; anything else unchanged. */
export function hnsLocationInRoot(root: HnsPathRef, ref: StorageResourceRef): StorageResourceRef {
  if (ref.kind !== 'blobPrefix' && ref.kind !== 'container') return ref;
  if (ref.container !== root.fileSystem) return ref;
  return hnsDirectoryOfBlobRoot(ref) ?? ref;
}
